/**
 * tools_in_hole — downhole. GET /dashboard/tools-in-hole (api#1141): runs
 * still in the hole, longest first, against the company's open-run alert
 * (open_run_alert_hours, set in Settings › Operations › Downhole).
 *
 * The hours are the API's `hours_in_hole`; nothing here subtracts a timestamp.
 * A run past the limit carries a red badge; "over the limit" is the API's
 * `over_limit`, not a comparison made here. The list holds at most eight, so
 * the footer says how many of how many when there are more.
 */
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { WidgetFigure, WidgetPanel } from '../WidgetPanel';
import { useToolsInHole } from '../overviewQueries';
import { VerticalOff, OpsBadge, OpsRow, OpsWidgetRead, dash } from '../OpsWidgetRead';
import type { ToolsInHole } from '../../../types/overview-operations';

export const fmtHoursInHole = (h: number | null) => (h === null || !Number.isFinite(h) ? '—' : `${Math.round(h)} h`);

function Body({ d }: { d: ToolsInHole }) {
  const navigate = useNavigate();
  return (
    <>
      <div className="grid grid-cols-3 gap-4">
        <WidgetFigure testId="tih-open" label="Runs in hole" value={d.open_runs} />
        <WidgetFigure testId="tih-over" label={`Over ${d.alert_hours} h`} value={d.over_limit} alert={d.over_limit > 0} />
        <WidgetFigure testId="tih-components" label="Tools in hole" value={d.components_in_hole} />
      </div>
      {d.open_runs === 0 ? (
        <p data-testid="tih-empty" className="mt-4 text-sm text-fieldiq-muted">No runs in the hole.</p>
      ) : (
        <div data-testid="tih-runs" className="mt-4 divide-y divide-fieldiq-border rounded-fi-control border border-fieldiq-border">
          {d.runs.map((r) => (
            <OpsRow key={r.id} to={r.link} onOpen={navigate} testId={`tih-run-${r.id}`}>
              <span className="min-w-0">
                <span className="block truncate font-semibold text-fieldiq-text">
                  Run {dash(r.run_number)} · {dash(r.assembly_number ?? r.assembly_name)}
                </span>
                <span className="block truncate text-fieldiq-muted">{dash(r.well_name)} · {dash(r.rig_name)}</span>
              </span>
              <span className="flex shrink-0 items-center gap-2">
                <span className="font-bold tabular-nums text-fieldiq-text">{fmtHoursInHole(r.hours_in_hole)}</span>
                {r.over_limit && <OpsBadge fill="bg-fi-signal-red" testId="tih-over-badge">Over limit</OpsBadge>}
              </span>
            </OpsRow>
          ))}
        </div>
      )}
      {d.runs.length < d.open_runs && (
        <p data-testid="tih-scope" className="mt-2 text-xs text-fieldiq-muted">
          Showing the {d.runs.length} longest of {d.open_runs} runs.
        </p>
      )}
    </>
  );
}

export default function ToolsInHoleWidget() {
  const { hasVertical } = useAuth();
  const on = hasVertical('downhole_tools');
  const q = useToolsInHole(on);
  return (
    <WidgetPanel widgetKey="tools_in_hole" eyebrow="Downhole now" title="Tools in the Hole" link={on ? { label: 'Run Logs', to: '/downhole/run-logs' } : undefined}>
      <div className="p-4">
        {!on ? (
          <VerticalOff testId="tih-vertical-off">Downhole is not on for this company.</VerticalOff>
        ) : (
        <OpsWidgetRead q={q} subject="tools in the hole" allowed="admins, office staff and dispatchers">
          {(d) => <Body d={d} />}
        </OpsWidgetRead>
        )}
      </div>
    </WidgetPanel>
  );
}
